import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { trpc } from "@/lib/trpc";
import DashboardLayout from "@/components/DashboardLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Separator } from "@/components/ui/separator";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { FileText, Download, AlertCircle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

/**
 * MyPA CBAM Page
 * Carbon Border Adjustment Mechanism form and report generation
 */
export default function CBAM() {
  const { t } = useTranslation();
  const [selectedPeriodId, setSelectedPeriodId] = useState<string>("");
  const { data: periods } = trpc.periods.list.useQuery();
  const { data: company } = trpc.company.get.useQuery();
  const { data: profile } = trpc.profile.get.useQuery();

  const exportCBAMMutation = trpc.reports.exportPDF.useMutation({
    onSuccess: (data) => {
      if (data.url) {
        window.open(data.url, '_blank');
        toast.success(t('cbam.report_generated_successfully'));
      }
    },
    onError: (error) => {
      toast.error(t('cbam.report_generation_failed', { error: error.message }));
    },
  });

  const [formData, setFormData] = useState({
    // Section 1: Exporter Information
    exporterCompanyName: "",
    exporterCountry: "",
    exporterAddress: "",
    exporterTIN: "",
    exporterContactName: "",
    exporterEmail: "",
    exporterPhone: "",
    
    // Section 2: EU Importer Information
    importerCompanyName: "",
    importerCountry: "",
    importerEORI: "",
    importerVAT: "",
    importerContactName: "",
    importerEmail: "",
    
    // Section 3: Product Information
    productCNCode: "",
    productDescription: "",
    productCategory: "",
    productQuantity: "",
    productUnit: "",
    productCustomsValue: "",
    shipmentDate: "",
    reportingQuarter: "",
    reportingYear: "",
    
    // Section 4: Carbon Pricing
    hasCarbonPricing: "",
    carbonPricingSystem: "",
    emissionsCovered: "",
    carbonPricePaid: "",
    carbonPriceCurrency: "",
    totalAmountPaid: "",
  });

  // Pré-remplir le formulaire depuis le profil entreprise et l'utilisateur
  useEffect(() => {
    if (company || profile) {
      setFormData(prev => ({
        ...prev,
        exporterCompanyName: company?.name || prev.exporterCompanyName,
        exporterCountry:     company?.country || company?.country_code || prev.exporterCountry,
        exporterAddress:     company?.address || prev.exporterAddress,
        exporterTIN:         company?.tax_id  || prev.exporterTIN,
        exporterContactName: `${profile?.first_name || ''} ${profile?.last_name || ''}`.trim() || prev.exporterContactName,
        exporterEmail:       profile?.email   || prev.exporterEmail,
        exporterPhone:       profile?.phone   || prev.exporterPhone,
      }));
    }
  }, [company, profile]);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleGenerateReport = () => {
    const requiredFields = [
      'exporterCompanyName', 'exporterCountry', 'exporterAddress', 'exporterTIN',
      'exporterContactName', 'exporterEmail', 'exporterPhone',
      'importerCompanyName', 'importerCountry', 'importerEORI', 'importerVAT',
      'importerContactName', 'importerEmail',
      'productCNCode', 'productDescription', 'productCategory', 'productQuantity',
      'productUnit', 'productCustomsValue', 'shipmentDate', 'reportingQuarter', 'reportingYear'
    ];
    const missingFields = requiredFields.filter(field => !formData[field as keyof typeof formData]);
    if (missingFields.length > 0) {
      toast.error(t('cbam.missing_required_fields'));
      return;
    }
    if (!selectedPeriodId) {
      toast.error(t('cbam.please_select_period'));
      return;
    }
    exportCBAMMutation.mutate({
      periodId: selectedPeriodId,
      template: 'cbam',
      cbamData: formData,
    });
  };

  const productCategories = [
    { value: "iron_steel", label: t('cbam.category_iron_steel') },
    { value: "cement", label: t('cbam.category_cement') },
    { value: "aluminum", label: t('cbam.category_aluminum') },
    { value: "fertilizers", label: t('cbam.category_fertilizers') },
    { value: "electricity", label: t('cbam.category_electricity') },
    { value: "hydrogen", label: t('cbam.category_hydrogen') },
  ];

  return (
    <DashboardLayout>
      <div className="space-y-6 pb-8">
        {/* Header */}
        <div className="space-y-2">
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
            <FileText className="h-8 w-8 text-primary" />
            {t('cbam.title')}
          </h1>
          <p className="text-muted-foreground text-lg">
            {t('cbam.subtitle')}
          </p>
        </div>

        {/* Important Notice */}
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {t('cbam.important_notice')}
          </AlertDescription>
        </Alert>

        {/* Sélecteur de période */}
        <Card>
          <CardHeader>
            <CardTitle>{t('cbam.select_period')}</CardTitle>
            <CardDescription>{t('cbam.select_period_desc')}</CardDescription>
          </CardHeader>
          <CardContent>
            <select
              value={selectedPeriodId}
              onChange={(e) => setSelectedPeriodId(e.target.value)}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              <option value="">{t('cbam.select_period_placeholder')}</option>
              {periods?.map((p: any) => (
                <option key={p.id} value={p.id}>
                  {p.period_label || p.label} — {p.year}
                </option>
              ))}
            </select>
          </CardContent>
        </Card>

        {/* Section 1: Exporter Information */}
        <Card>
          <CardHeader>
            <CardTitle>{t('cbam.section1_title')}</CardTitle>
            <CardDescription>{t('cbam.section1_desc')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="exporterCompanyName">
                  {t('cbam.exporter_company_name')} <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="exporterCompanyName"
                  value={formData.exporterCompanyName}
                  onChange={(e) => handleInputChange('exporterCompanyName', e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="exporterCountry">
                  {t('cbam.exporter_country')} <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="exporterCountry"
                  value={formData.exporterCountry}
                  onChange={(e) => handleInputChange('exporterCountry', e.target.value)}
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="exporterAddress">
                {t('cbam.exporter_address')} <span className="text-red-500">*</span>
              </Label>
              <Input
                id="exporterAddress"
                value={formData.exporterAddress}
                onChange={(e) => handleInputChange('exporterAddress', e.target.value)}
              />
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="exporterTIN">
                  {t('cbam.exporter_tin')} <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="exporterTIN"
                  value={formData.exporterTIN}
                  onChange={(e) => handleInputChange('exporterTIN', e.target.value)}
                  placeholder={t('cbam.exporter_tin_placeholder')}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="exporterContactName">
                  {t('cbam.contact_name')} <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="exporterContactName"
                  value={formData.exporterContactName}
                  onChange={(e) => handleInputChange('exporterContactName', e.target.value)}
                />
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="exporterEmail">
                  {t('cbam.contact_email')} <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="exporterEmail"
                  type="email"
                  value={formData.exporterEmail}
                  onChange={(e) => handleInputChange('exporterEmail', e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="exporterPhone">
                  {t('cbam.contact_phone')} <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="exporterPhone"
                  value={formData.exporterPhone}
                  onChange={(e) => handleInputChange('exporterPhone', e.target.value)}
                  placeholder={t('cbam.contact_phone_placeholder')}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Section 2: EU Importer Information */}
        <Card>
          <CardHeader>
            <CardTitle>{t('cbam.section2_title')}</CardTitle>
            <CardDescription>{t('cbam.section2_desc')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="importerCompanyName">
                  {t('cbam.importer_company_name')} <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="importerCompanyName"
                  value={formData.importerCompanyName}
                  onChange={(e) => handleInputChange('importerCompanyName', e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="importerCountry">
                  {t('cbam.importer_country')} <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="importerCountry"
                  value={formData.importerCountry}
                  onChange={(e) => handleInputChange('importerCountry', e.target.value)}
                />
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="importerEORI">
                  {t('cbam.importer_eori')} <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="importerEORI"
                  value={formData.importerEORI}
                  onChange={(e) => handleInputChange('importerEORI', e.target.value)}
                  placeholder={t('cbam.importer_eori_placeholder')}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="importerVAT">
                  {t('cbam.importer_vat')} <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="importerVAT"
                  value={formData.importerVAT}
                  onChange={(e) => handleInputChange('importerVAT', e.target.value)}
                />
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="importerContactName">
                  {t('cbam.contact_name')} <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="importerContactName"
                  value={formData.importerContactName}
                  onChange={(e) => handleInputChange('importerContactName', e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="importerEmail">
                  {t('cbam.contact_email')} <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="importerEmail"
                  type="email"
                  value={formData.importerEmail}
                  onChange={(e) => handleInputChange('importerEmail', e.target.value)}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Section 3: Product Information */}
        <Card>
          <CardHeader>
            <CardTitle>{t('cbam.section3_title')}</CardTitle>
            <CardDescription>{t('cbam.section3_desc')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="productCNCode">
                  {t('cbam.product_cn_code')} <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="productCNCode"
                  value={formData.productCNCode}
                  onChange={(e) => handleInputChange('productCNCode', e.target.value)}
                  placeholder={t('cbam.product_cn_code_placeholder')}
                  maxLength={8}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="productCategory">
                  {t('cbam.product_category')} <span className="text-red-500">*</span>
                </Label>
                <select
                  id="productCategory"
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  value={formData.productCategory}
                  onChange={(e) => handleInputChange('productCategory', e.target.value)}
                >
                  <option value="">{t('cbam.select_category')}</option>
                  {productCategories.map(cat => (
                    <option key={cat.value} value={cat.value}>{cat.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="productDescription">
                {t('cbam.product_description')} <span className="text-red-500">*</span>
              </Label>
              <Input
                id="productDescription"
                value={formData.productDescription}
                onChange={(e) => handleInputChange('productDescription', e.target.value)}
              />
            </div>

            <div className="grid gap-4 md:grid-cols-3">
              <div className="space-y-2">
                <Label htmlFor="productQuantity">
                  {t('cbam.product_quantity')} <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="productQuantity"
                  type="number"
                  value={formData.productQuantity}
                  onChange={(e) => handleInputChange('productQuantity', e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="productUnit">
                  {t('cbam.product_unit')} <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="productUnit"
                  value={formData.productUnit}
                  onChange={(e) => handleInputChange('productUnit', e.target.value)}
                  placeholder={t('cbam.product_unit_placeholder')}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="productCustomsValue">
                  {t('cbam.product_customs_value')} <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="productCustomsValue"
                  type="number"
                  value={formData.productCustomsValue}
                  onChange={(e) => handleInputChange('productCustomsValue', e.target.value)}
                  placeholder="EUR"
                />
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-3">
              <div className="space-y-2">
                <Label htmlFor="shipmentDate">
                  {t('cbam.shipment_date')} <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="shipmentDate"
                  type="date"
                  value={formData.shipmentDate}
                  onChange={(e) => handleInputChange('shipmentDate', e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="reportingQuarter">
                  {t('cbam.reporting_quarter')} <span className="text-red-500">*</span>
                </Label>
                <select
                  id="reportingQuarter"
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  value={formData.reportingQuarter}
                  onChange={(e) => handleInputChange('reportingQuarter', e.target.value)}
                >
                  <option value="">{t('cbam.select_quarter')}</option>
                  <option value="Q1">Q1 (Jan-Mar)</option>
                  <option value="Q2">Q2 (Apr-Jun)</option>
                  <option value="Q3">Q3 (Jul-Sep)</option>
                  <option value="Q4">Q4 (Oct-Dec)</option>
                </select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="reportingYear">
                  {t('cbam.reporting_year')} <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="reportingYear"
                  type="number"
                  value={formData.reportingYear}
                  onChange={(e) => handleInputChange('reportingYear', e.target.value)}
                  placeholder="2025"
                  min="2023"
                  max="2030"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Section 4: Carbon Pricing (Optional) */}
        <Card>
          <CardHeader>
            <CardTitle>{t('cbam.section4_title')}</CardTitle>
            <CardDescription>{t('cbam.section4_desc')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert className="bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-800">
              <AlertDescription className="text-blue-900 dark:text-blue-100">
                {t('cbam.carbon_pricing_notice')}
              </AlertDescription>
            </Alert>

            <div className="space-y-2">
              <Label>{t('cbam.has_carbon_pricing')}</Label>
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <input
                    type="radio"
                    id="pricing-not-sure"
                    name="hasCarbonPricing"
                    value="not_sure"
                    checked={formData.hasCarbonPricing === "not_sure"}
                    onChange={(e) => handleInputChange('hasCarbonPricing', e.target.value)}
                  />
                  <Label htmlFor="pricing-not-sure" className="font-normal">
                    {t('cbam.pricing_not_sure')}
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    type="radio"
                    id="pricing-ets"
                    name="hasCarbonPricing"
                    value="ets"
                    checked={formData.hasCarbonPricing === "ets"}
                    onChange={(e) => handleInputChange('hasCarbonPricing', e.target.value)}
                  />
                  <Label htmlFor="pricing-ets" className="font-normal">
                    {t('cbam.pricing_ets')}
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    type="radio"
                    id="pricing-tax"
                    name="hasCarbonPricing"
                    value="tax"
                    checked={formData.hasCarbonPricing === "tax"}
                    onChange={(e) => handleInputChange('hasCarbonPricing', e.target.value)}
                  />
                  <Label htmlFor="pricing-tax" className="font-normal">
                    {t('cbam.pricing_tax')}
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    type="radio"
                    id="pricing-no"
                    name="hasCarbonPricing"
                    value="no"
                    checked={formData.hasCarbonPricing === "no"}
                    onChange={(e) => handleInputChange('hasCarbonPricing', e.target.value)}
                  />
                  <Label htmlFor="pricing-no" className="font-normal">
                    {t('cbam.pricing_no')}
                  </Label>
                </div>
              </div>
            </div>

            {(formData.hasCarbonPricing === "ets" || formData.hasCarbonPricing === "tax") && (
              <>
                <Separator />
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="carbonPricingSystem">
                      {t('cbam.carbon_pricing_system')}
                    </Label>
                    <Input
                      id="carbonPricingSystem"
                      value={formData.carbonPricingSystem}
                      onChange={(e) => handleInputChange('carbonPricingSystem', e.target.value)}
                    />
                  </div>

                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="emissionsCovered">
                        {t('cbam.emissions_covered')}
                      </Label>
                      <Input
                        id="emissionsCovered"
                        type="number"
                        value={formData.emissionsCovered}
                        onChange={(e) => handleInputChange('emissionsCovered', e.target.value)}
                        placeholder="tCO₂e"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="carbonPricePaid">
                        {t('cbam.carbon_price_paid')}
                      </Label>
                      <Input
                        id="carbonPricePaid"
                        type="number"
                        value={formData.carbonPricePaid}
                        onChange={(e) => handleInputChange('carbonPricePaid', e.target.value)}
                        placeholder={t('cbam.carbon_price_paid_placeholder')}
                      />
                    </div>
                  </div>

                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="carbonPriceCurrency">
                        {t('cbam.currency')}
                      </Label>
                      <Input
                        id="carbonPriceCurrency"
                        value={formData.carbonPriceCurrency}
                        onChange={(e) => handleInputChange('carbonPriceCurrency', e.target.value)}
                        placeholder="EUR, USD, etc."
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="totalAmountPaid">
                        {t('cbam.total_amount_paid')}
                      </Label>
                      <Input
                        id="totalAmountPaid"
                        type="number"
                        value={formData.totalAmountPaid}
                        onChange={(e) => handleInputChange('totalAmountPaid', e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex justify-end gap-4">
              <Button variant="outline" onClick={() => window.print()}>
                <Download className="mr-2 h-4 w-4" />
                {t('cbam.download_form')}
              </Button>
              <Button
                onClick={handleGenerateReport}
                disabled={exportCBAMMutation.isPending}
                className="bg-gradient-mypa hover:opacity-90"
              >
                <CheckCircle2 className="mr-2 h-4 w-4" />
                {exportCBAMMutation.isPending ? t('cbam.generating') : t('cbam.generate_report')}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Next Steps */}
        <Alert className="bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-800">
          <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
          <AlertDescription className="text-green-900 dark:text-green-100">
            <strong>{t('cbam.next_steps_title')}</strong>
            <ul className="mt-2 space-y-1 text-sm">
              <li>✓ {t('cbam.next_step1')}</li>
              <li>✓ {t('cbam.next_step2')}</li>
              <li>✓ {t('cbam.next_step3')}</li>
              <li>✓ {t('cbam.next_step4')}</li>
            </ul>
          </AlertDescription>
        </Alert>
      </div>
    </DashboardLayout>
  );
}
